/* eslint-disable */
import { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";

import { CancelablePromise, type OpenAPIConfig, type ApiRequestOptions } from "@/interfaces/api";
import { getAxiosInstance } from "@lib/utils/axiosInstance";
import { BLOB_ENDPOINTS } from "@lib/utils/api";

type ResolveFN<T> = (value: T | PromiseLike<T>) => void;
type RejectFN = (reason?: any) => void;
type CancelFN = (cancelHandler: () => void) => void;

const reqs: Record<string, CancelablePromise<any>> = {};

export const request = <T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
): CancelablePromise<AxiosResponse<T>> => {
  let path = options.url;
  if (options.path) {
    Object.keys(options.path).forEach((key) => {
      path = path.replace(`{${key}}`, (options.path ?? {})[key]);
    });
  }
  let query = "";
  if (typeof options.query !== "undefined" && Object.keys(options.query).length > 0) {
    query = `?${Object.keys(options.query)
      .map((key) => {
        const val = (options.query ?? {})[key];
        return typeof val !== "undefined" && val !== null ? `${key}=${val}` : null;
      })
      .filter((str) => typeof str !== "undefined" && str !== null)
      .join("&")}`;
  }
  const url = `${config.BASE}${path}${query}`;
  const key = `${options.method}:${url}:${JSON.stringify(options.body ?? {})}:${JSON.stringify(options.headers ?? {})}`;

  if (!reqs[key]) {
    reqs[key] = new CancelablePromise((resolve: ResolveFN<any>, reject: RejectFN, onCancel: CancelFN) => {
      // Get the request URL. Depending on your needs, this might need additional processing,
      // @see ./src/templates/core/functions/getUrl.hbs
      let body = options.body;

      if (options.formData && options.mediaType === "multipart/form-data") {
        const formDataKeys = Object.keys(options.formData);
        const form = new FormData();
        formDataKeys.forEach((key) => {
          if (options.formData?.hasOwnProperty(key)) {
            form.append(key, options.formData[key]);
          }
        });
        body = form;
      }

      // Optional: Get and link the cancelation token, so the request can be aborted.
      const controller = new AbortController();
      onCancel(() => controller.abort());

      // ToDo: take a look at https://github.com/ferdikoomen/openapi-typescript-codegen/issues/1739
      // and implement the `isBlob` automatically after it's done
      const isBlob = BLOB_ENDPOINTS.some((endpoint) => options.method === endpoint[0] && options.url === endpoint[1]);

      // Execute the request. This is a minimal example, in real world scenarios
      // you will need to add headers, process form data, etc.
      // @see ./src/templates/core/axios/request.hbs
      getAxiosInstance()
        .request({
          url,
          data: body,
          method: options.method,
          headers: options.headers ?? {},
          signal: controller.signal,
          responseType: isBlob ? "blob" : "json",
        } as AxiosRequestConfig)
        .then((data: AxiosResponse<T>) => {
          delete reqs[key];
          resolve(data?.data);
        })
        .catch((error: AxiosError) => {
          delete reqs[key];
          reject(error);
        });
    });
  }

  return reqs[key];
};
